import { useMemo, useState } from 'react'
import { generatePath, Link } from 'react-router-dom'

import { Breadcrumbs } from '@/common/Breadcrumbs'
import { formatUtc } from '@/helpers'
import { canVoteOnProposal } from '@/helpers/governance'
import { useProposals } from '@/queries/governance'
import { RoutePaths } from '@/routes'
import { ProposalStatus } from '@/types/governance'
import { UiCard } from '@/ui/UiCard'
import UiContainer from '@/ui/UiContainer'
import {
  UiSelect,
  UiSelectContent,
  UiSelectItem,
  UiSelectTrigger,
  UiSelectValue,
} from '@/ui/UiSelect'
import { UiSkeleton } from '@/ui/UiSkeleton'

import GovernanceProposalStatusBadge from './components/GovernanceProposalStatusBadge'
import ProposalVoteCounts from './components/ProposalVoteCounts'

export default function ProposalsListPage() {
  const [statusFilter, setStatusFilter] = useState<ProposalStatus | 'all'>(
    'all',
  )

  // Backend filters don't work, so we fetch all and filter on frontend
  const { data, isLoading, isError } = useProposals({
    pagination: { limit: '100', reverse: true },
  })

  const filteredProposals = useMemo(() => {
    if (!data?.proposals) return []
    if (statusFilter === 'all') return data.proposals
    return data.proposals.filter(p => p.status === statusFilter)
  }, [data?.proposals, statusFilter])

  const breadcrumbsItems = [
    { label: 'Home', to: RoutePaths.Root },
    { label: 'Governance' },
  ]

  return (
    <UiContainer>
      <div className='mt-26 space-y-6 px-4 md:px-0'>
        <div className='bg-background sticky top-0 z-10 flex flex-col gap-6 pt-2 pb-4'>
          <Breadcrumbs items={breadcrumbsItems} />

          <div className='flex flex-wrap items-start justify-between gap-4'>
            <h1 className='text-foreground text-xl font-semibold md:text-2xl'>
              Governance Proposals
            </h1>
          </div>

          {/* Filter */}
          <div className='flex items-center gap-4'>
            <label className='text-foreground text-sm font-medium'>
              Filter by status:
            </label>
            <UiSelect
              value={statusFilter}
              onValueChange={value =>
                setStatusFilter(value as ProposalStatus | 'all')
              }
            >
              <UiSelectTrigger className='text-foreground w-48'>
                <UiSelectValue placeholder='All statuses' />
              </UiSelectTrigger>
              <UiSelectContent>
                <UiSelectItem value='all'>All Statuses</UiSelectItem>
                <UiSelectItem value={ProposalStatus.VotingPeriod}>
                  Voting Period
                </UiSelectItem>
                <UiSelectItem value={ProposalStatus.Passed}>
                  Passed
                </UiSelectItem>
                <UiSelectItem value={ProposalStatus.Rejected}>
                  Rejected
                </UiSelectItem>
                <UiSelectItem value={ProposalStatus.DepositPeriod}>
                  Deposit Period
                </UiSelectItem>
                <UiSelectItem value={ProposalStatus.Failed}>
                  Failed
                </UiSelectItem>
                <UiSelectItem value={ProposalStatus.Canceled}>
                  Canceled
                </UiSelectItem>
              </UiSelectContent>
            </UiSelect>
          </div>
        </div>

        {/* List */}
        {(() => {
          if (isLoading) {
            return (
              <div className='space-y-4'>
                {[...Array(5)].map((_, index) => (
                  <UiCard key={index}>
                    <div className='flex flex-wrap items-center gap-3 p-6'>
                      <UiSkeleton className='h-6 w-32' />
                      <UiSkeleton className='ml-auto h-6 w-24' />
                      <div className='w-full sm:flex sm:flex-1 sm:justify-center'>
                        <div className='flex flex-wrap gap-x-10 gap-y-3'>
                          <UiSkeleton className='h-10 w-40' />
                          <UiSkeleton className='h-10 w-40' />
                          <UiSkeleton className='h-10 w-40' />
                        </div>
                      </div>
                    </div>
                  </UiCard>
                ))}
              </div>
            )
          }

          if (isError) {
            return (
              <UiCard className='p-6 text-center'>
                <p className='text-foreground text-sm font-semibold'>
                  Failed to load proposals. Please try again later.
                </p>
              </UiCard>
            )
          }

          if (!data?.proposals || filteredProposals.length === 0) {
            return (
              <UiCard className='p-6 text-center'>
                <p className='text-muted-foreground text-sm'>
                  {statusFilter === 'all'
                    ? 'No proposals found.'
                    : 'No proposals with this status.'}
                </p>
              </UiCard>
            )
          }

          return (
            <div className='space-y-4'>
              {filteredProposals.map(proposal => {
                const isVotingActive = canVoteOnProposal(proposal.status)

                return (
                  <Link
                    key={proposal.id}
                    to={generatePath(RoutePaths.GovernanceProposal, {
                      proposalId: proposal.id,
                    })}
                    className='group block'
                  >
                    <UiCard className='hover:border-border/90 hover:bg-card/45 relative overflow-hidden transition-all'>
                      {isVotingActive && (
                        <>
                          <div className='bg-accent absolute top-0 left-0 h-full w-1' />
                          <div className='bg-accent/20 group-hover:bg-accent/30 absolute top-0 left-0 h-full w-full transition-colors' />
                        </>
                      )}
                      <div className='relative flex flex-wrap items-center gap-3 p-6'>
                        <div className='order-1 flex min-w-0 items-center gap-2'>
                          <p className='font-mono text-base font-semibold'>
                            Proposal #{proposal.id}
                          </p>
                          {isVotingActive && (
                            <span className='bg-accent/80 relative flex h-2 w-2 rounded-full'>
                              <span className='bg-accent absolute inline-flex h-full w-full animate-ping rounded-full opacity-75' />
                            </span>
                          )}
                        </div>

                        <div className='order-2 ml-auto shrink-0 sm:order-3'>
                          <GovernanceProposalStatusBadge
                            status={proposal.status}
                          />
                        </div>

                        <div className='order-3 w-full sm:order-2 sm:flex sm:flex-1 sm:justify-center'>
                          <div className='flex flex-wrap items-start gap-x-10 gap-y-3'>
                            <div className='flex min-w-40 flex-col gap-1 text-sm'>
                              <span className='text-muted-foreground'>
                                {isVotingActive ? 'Voting ends:' : 'Submitted:'}
                              </span>
                              <span className='text-foreground font-medium'>
                                {formatUtc(
                                  new Date(
                                    isVotingActive
                                      ? proposal.voting_end_time
                                      : proposal.submit_time,
                                  ),
                                  'yyyy-MM-dd HH:mm',
                                )}
                              </span>
                            </div>

                            <ProposalVoteCounts proposalId={proposal.id} />
                          </div>
                        </div>
                      </div>
                    </UiCard>
                  </Link>
                )
              })}
            </div>
          )
        })()}
      </div>
    </UiContainer>
  )
}
